<template>
    <div class="d-flex flex-column gap-4">
        <!-- SPINNERS -->
        <SpinnerWithLabel v-if="isSigningIn" class="justify-content-center">
            {{ __('auth.wait_for_sign_in') }}
        </SpinnerWithLabel>
        <SpinnerWithLabel v-if="isCompletingSignIn" class="justify-content-center">
            {{ __('auth.youre_being_signed_in') }}
        </SpinnerWithLabel>

        <!-- ERRORS -->
        <ErrorBox v-if="error !== null"
                  :error="error"
        />
        <div v-if="showVerifyEmail"
             class="bg-light-warning text-warning rounded p-8">
            <div class="font-size-lg font-weight-bold">
                {{ __('auth.please_confirm_email') }}
            </div>
            {{ __('auth.verify_email') }}
            <button class="btn btn-sm btn-block btn-warning mt-4"
                    :disabled="isRequestingVerificationMail"
                    @click="requestEmailVerification(localUser, true)">
                <i class="fas fa-paper-plane"/>
                {{ __('auth.resend_verification_link') }}
            </button>
            <button class="btn btn-sm btn-block btn-outline-warning mt-2"
                    :disabled="isRequestingVerificationMail"
                    @click="retryLogin">
                {{ __('auth.retry_login') }}
            </button>
        </div>

        <!-- AUTH PROVIDER BUTTONS -->
        <div
            v-if="authenticatorIsInitialized && showProviders && !userIsAuthenticated && !isLoginWithEmail && !requiresMfa && !showHints"
            class="d-flex flex-column gap-2">
            <LoginProviderButton v-for="provider in providers"
                                 :key="provider.type"
                                 :provider-name="provider.name"
                                 :icon="provider.icon"
                                 :bg-color="provider.bgColor"
                                 :border-color="provider.borderColor"
                                 :fg-color="provider.fgColor"
                                 @click="selectSignInMethod(provider.type)"
            />
        </div>

        <!-- LOGIN/REGISTER FORM -->
        <div v-if="showLoginForm && isLoginWithEmail && !requiresMfa && !showHints && !showVerifyEmail"
             class="d-flex flex-column gap-4">
            <form class="d-flex flex-column gap-4">
                <FormGroupNew v-if="registerWithEmail"
                              :label="__('user.display_name')"
                              :error="errors.display_name"
                              no-margin
                              required
                >
                    <input ref="name"
                           class="form-control shadow-xs"
                           type="text"
                           :placeholder="__('general.name')"
                           v-model="user.name"
                    >
                </FormGroupNew>
                <FormGroupNew :label="__('general.email')"
                              :error="errors.email"
                              no-margin
                              required
                >
                    <input ref="email"
                           class="form-control shadow-xs"
                           name="email"
                           type="email"
                           autocomplete="username"

                           :placeholder="__('general.email')"
                           v-model="user.email"
                    >
                </FormGroupNew>
                <FormGroupNew :label="__('user.password')"
                              :error="errors.password"
                              no-margin
                              required
                >
                    <input ref="password"
                           class="form-control shadow-xs"
                           type="password"
                           name="password"
                           autocomplete="current-password"
                           :placeholder="__('user.password')"
                           v-model="user.password"
                           @keyup.enter="loginWithEmail"
                    >
                </FormGroupNew>
            </form>
            <div/>
            <div class="d-flex flex-column gap-2">
                <button v-if="!registerWithEmail" class="btn btn-primary shadow-xs" @click="loginWithEmail">
                    <i class="fas fa-sign-in-alt"/>
                    {{ __('actions.sign_in') }}
                </button>
                <DividerWithTitle v-if="!registerWithEmail" :title="__('general.or')"/>
                <button class="btn btn-light-primary" @click="register">
                    <i class="fas fa-user-plus"/>
                    {{ __('actions.create_thing', {thing: __('user.account')}) }}
                </button>
                <button v-if="!registerWithEmail" class="btn btn-light-primary" @click="resetPassword">
                    <i class="fas fa-undo"/>
                    {{ __('auth.reset_password') }}
                </button>
                <button class="btn bg-light-light btn-hover-warning" @click="abort">
                    {{ __('actions.abort') }}
                </button>
            </div>
        </div>

        <!-- Select MFA provider -->
        <div v-if="showHints">
            <SelectMfaProvider :hints="hints" @select="continueSignInWithHint"/>
        </div>

        <!-- MFA CODE -->
        <div v-if="requiresMfa" class="d-flex flex-column gap-4">
            <FormGroupNew :label="__('user.mfa')"
                          :description="__('auth.provide_code_that_was_sent', {phone: selectedHint.phoneNumber})"
                          no-margin
                          required>
                <input ref="mfa"
                       class="form-control shadow-xs"
                       type="text"
                       :placeholder="__('auth.mfa_code')"
                       v-model="user.mfaCode"
                       @keyup.enter="verifyMfaAndSignIn"
                >
            </FormGroupNew>
            <button v-if="!registerWithEmail"
                    class="btn btn-primary shadow-xs"
                    :disabled="user.mfaCode.length === 0"
                    @click="verifyMfaAndSignIn"
            >
                <i class="fas fa-sign-in-alt"/>
                {{ __('actions.sign_in') }}
            </button>
        </div>

        <!-- LEGAL INFO -->
        <div class="font-size-sm text-center text-muted" v-html="footerContent"/>
        <div ref="reCaptcha"/>
    </div>
</template>

<script>
import LoginProviderButton from "@components/Auth/LoginProviderButton.vue";
import authMixin from "@mixins/authMixin";
import FormGroupNew from "@components/Form/FormGroupNew.vue";
import {Gender} from "@generated/gender";
import DividerDotted from "@components/Form/DividerDotted.vue";
import DividerWithTitle from "@components/DividerWithTitle.vue";
import SpinnerWithLabel from "@components/SpinnerWithLabel.vue";
import {
    AuthErrorCodes,
    createUserWithEmailAndPassword,
    getMultiFactorResolver,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier
} from "firebase/auth";
import SelectMfaProvider from "@components/Auth/SelectMfaProvider.vue";
import ErrorBox from "@components/Auth/ErrorBox.vue";
import {LoginErrors} from "@generated/loginErrors";

export default {
    components: {
        ErrorBox,
        SelectMfaProvider,
        SpinnerWithLabel,
        DividerWithTitle,
        DividerDotted,
        FormGroupNew,
        LoginProviderButton
    },

    mixins: [authMixin],

    props: {
        redirectAfterAuthentication: Boolean
    },

    data() {
        return {
            auth: Object,
            showProviders: true,
            isLoginWithEmail: false,
            isCompletingSignIn: false,
            isRequestingVerificationMail: false,
            registerWithEmail: false,
            requiresMfa: false,
            showLoginForm: false,
            showVerifyEmail: false,
            hints: [],
            selectedHint: null,
            showHints: false,
            resolver: null,
            verificationId: null,
            user: {
                gender: Gender.DIVERS,
                name: '',
                email: '',
                password: '',
                mfaCode: '',
            },
            errors: {
                email: null,
                display_name: null,
                password: null,
            },
            error: null,
            providers: [
                {
                    type: 'email',
                    name: 'E-Mail',
                    icon: 'fas fa-envelope',
                    bgColor: '#dc4437',
                    borderColor: '#c93e32',
                    fgColor: '#ffffff',
                },
                {
                    type: 'google',
                    name: 'Google',
                    icon: 'fab fa-google',
                    bgColor: '#ffffeff',
                    borderColor: '#f6f6f6',
                    fgColor: '#333333'
                },
                {
                    type: 'facebook',
                    name: 'Facebook',
                    icon: 'fab fa-facebook',
                    bgColor: '#3c5999',
                    borderColor: '#32497e',
                    fgColor: '#ffffff'
                }
            ]
        }
    },

    mounted() {
        this.setReCaptchaContainer(this.$refs.reCaptcha)
        this.enableSignInWithApple();
    },

    methods: {
        anchor(url, name) {
            return `<a href="${url}" target="_new">${name}</a>`
        },

        selectSignInMethod(type) {
            this.clearErrorBox();
            if (type === 'email') {
                this.isLoginWithEmail = true;
                this.showLoginForm = true;
                this.$nextTick(() => this.$refs.email.focus());
            } else if (type === 'google') {
                this.attemptSignInWithGoogle(this.showMfaField, this.verifyAndRedirect, this.handleAuthenticationError);
                this.hideProviders();
            } else if (type === 'facebook') {
                this.attemptSignInWithFacebook(this.showMfaField, this.verifyAndRedirect, this.handleAuthenticationError);
                this.hideProviders();
            } else if (type === 'apple') {
                this.attemptSignInWithApple(this.showMfaField, this.verifyAndRedirect, this.handleAuthenticationError);
                this.hideProviders();
            } else {
                console.error('Invalid sign in method selected: ', type);
            }
        },

        abort() {
            this.user = {
                gender: Gender.DIVERS,
                name: '',
                email: '',
                password: '',
                mfaCode: ''
            };
            this.registerWithEmail = false;
            this.isLoginWithEmail = false;
            this.requiresMfa = false;
            this.clearErrorBox();
        },

        register() {
            this.errors.email = null;
            this.errors.password = null;
            this.errors.display_name = null;

            this.clearErrorBox();
            if (!this.registerWithEmail) {
                this.registerWithEmail = true;
                return;
            }

            let hasValidationErrors = false;
            if (this.user.name.trim().length === 0) {
                this.errors.display_name = __('general.thing.cant_be_empty', {thing: __('user.display_name')});
                hasValidationErrors = true;
            } else {
                this.errors.display_name = null;
            }

            if (hasValidationErrors || this.emailAndPasswordValidationFail()) {
                return;
            }

            createUserWithEmailAndPassword(this.authenticator, this.user.email, this.user.password)
                .then(userCredentials => {
                    this.showLoginForm = false;
                    this.clearErrorBox();
                    this.updateUserDisplayName(userCredentials.user, this.user.name)
                    if (!userCredentials.user.emailVerified) {
                        this.requestEmailVerification(userCredentials.user, true)
                    }
                })
                .catch(this.handleAuthenticationError);
        },

        emailAndPasswordValidationFail() {
            let hasValidationErrors = false;

            if (this.user.email.trim().length === 0) {
                this.errors.email = __('general.thing.cant_be_empty', {thing: __('user.email')});
                hasValidationErrors = true;
            } else {
                this.errors.email = null;
            }

            if (this.user.password.trim().length === 0) {
                this.errors.password = __('general.thing.cant_be_empty', {thing: __('user.password')});
                hasValidationErrors = true;
            } else {
                this.errors.password = null;
            }

            return hasValidationErrors;
        },

        requestEmailVerification(user, triggerSend = false) {
            this.showVerifyEmail = true;
            this.isCompletingSignIn = false;

            if (triggerSend) {
                this.isRequestingVerificationMail = true;
                this.sendVerificationEmail(
                    user,
                    () => {
                        this.showVerifyEmail = true
                        this.isRequestingVerificationMail = false;
                    },
                    (error) => {
                        console.error('[Auth] Failed to send verification mail.', error)
                    }
                );
            }
        },

        resetPassword() {
            this.errors.password = null;
            if (this.user.email.trim().length === 0) {
                this.errors.email = __('general.thing.cant_be_empty', {thing: __('user.email')});
                return;
            } else {
                this.errors.email = null;
            }

            this.requestPasswordReset(
                this.user.email,
                () => {
                    toastr.success(__('auth.password_reset_requested'))
                },
                this.handleAuthenticationError
            )
        },

        loginWithEmail() {
            this.clearErrorBox();
            this.showVerifyEmail = false;
            if (this.emailAndPasswordValidationFail()) {
                return;
            }
            this.attemptSignInWithCredentials(this.user.email, this.user.password, this.showMfaField, this.verifyAndRedirect, this.handleAuthenticationError)
        },

        retryLogin() {
            this.showVerifyEmail = false;
            this.attemptSignInWithCredentials(this.user.email, this.user.password, this.showMfaField, this.verifyAndRedirect, this.handleAuthenticationError)
        },

        handleAuthenticationError(error) {
            if (error.code === AuthErrorCodes.INVALID_PASSWORD) {
                axios.post(this.$route('login.hit'), {u: this.user.email});
            }

            switch (error.code) {
                case AuthErrorCodes.MFA_REQUIRED:
                    this.initializeMfa(error);
                    break;

                case AuthErrorCodes.UNVERIFIED_EMAIL:
                    this.showVerifyEmail = true;
                    break;

                default:
                    console.error('[Auth] Error while authenticating.', error)
                    this.error = this.mapAuthError(error)
            }
        },

        handleDashboardSingInError(error) {
            console.error('Failed to sign in.', error);

            this.isCompletingSignIn = false;

            if (error.response.status === 419) {
                //CSRF Token expired.
                console.error(
                    __('auth.session_expired'),
                    __('auth.please_reload_page')
                );
                return;
            }

            if (error.response.data.hasOwnProperty('code')) {
                let errorCode = error.response.data.code;

                if (errorCode === LoginErrors.EMAIL_NOT_VERIFIED) {
                    //show verify email
                    this.showVerifyEmail = true;
                    return;
                } else {
                    this.showVerifyEmail = false;
                }

                this.displayError(
                    __('auth.errors.' + errorCode),
                    __('auth.please_try_again_later')
                );
            } else {
                this.displayError(
                    __('general.unknown_error'),
                    __('auth.please_try_again_later')
                );
            }
        },

        displayError(title, message) {
            this.error = {title: title, message: message};
        },

        clearErrorBox() {
            this.error = null;
        },

        continueSignInWithHint(hint) {
            this.selectedHint = hint;
            this.showHints = false;

            const recaptchaVerifier = new RecaptchaVerifier(this.authenticator, this.reCaptchaContainer, {
                'size': 'invisible',
            });

            const phoneInfoOptions = {
                multiFactorHint: hint,
                session: this.resolver.session
            };

            const phoneAuthProvider = new PhoneAuthProvider(this.authenticator);
            phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
                .then(this.showMfaField);
        },

        hideProviders() {
            this.showProviders = false;
        },

        initializeMfa(error) {
            this.resolver = getMultiFactorResolver(this.authenticator, error);
            this.hints = this.resolver.hints;

            if (this.hints.length > 1) {
                this.showHints = true;
            } else if (this.hints.length === 0) {
                this.displayError('Missing provider', "Couldn't find device for authentication.")
            } else {
                this.selectedHint = this.hints[0];
                this.continueSignInWithHint(this.selectedHint);
            }
        },

        showMfaField(verificationId) {
            this.requiresMfa = true;
            this.showLoginForm = false;
            this.verificationId = verificationId;
            this.$nextTick(() => this.$refs.mfa.focus());
        },

        verifyAndRedirect(user, idToken) {
            this.isCompletingSignIn = true;

            if (!this.isUserEmailVerified(user)) {
                this.requestEmailVerification(user);
                return;
            }

            this.showVerifyEmail = false;
            this.$emit('authenticated', this.localUser);
            if (this.redirectAfterAuthentication) {
                this.isCompletingSignIn = true;
                this.clearErrorBox();
                this.attemptDashboardSignIn(idToken, this.handleDashboardSingInError);
            }
        },

        verifyMfaAndSignIn() {
            this.clearErrorBox();
            const credentials = PhoneAuthProvider.credential(this.verificationId, this.user.mfaCode);
            this.resolver.resolveSignIn(PhoneMultiFactorGenerator.assertion(credentials))
                .then(userCredential => {
                    this.$emit('authenticated', this.localUser);
                    if (this.redirectAfterAuthentication) {
                        this.isCompletingSignIn = true;
                        this.attemptDashboardSignIn(userCredential._tokenResponse.idToken, this.handleDashboardSingInError)
                    }
                })
                .catch(this.handleAuthenticationError);
        },

        enableSignInWithApple() {
            if (this.userIsOnMacOs) {
                this.providers.push({
                    type: 'apple',
                    name: 'Apple',
                    icon: 'fab fa-apple',
                    bgColor: '#333333',
                    borderColor: '#111111',
                    fgColor: '#eeeeee'
                });
            }
        }
    },

    computed: {
        Gender() {
            return Gender
        },

        userIsOnMacOs() {
            //TODO: detect macos properly
            return false;
        },

        availableGenders() {
            let available = {};
            for (let genderId of Object.values(Gender)) {
                if (genderId === Gender.DIVERS) {
                    available[__('general.none')] = genderId;
                } else {
                    available[__('user.salutations.' + genderId)] = genderId;
                }
            }
            return available;
        },

        footerContent() {
            return __('auth.by_continuing_you_agree_to_tos', {
                tos: this.anchor(this.getTermsOfServiceUrl(), __('general.tos')),
                pp: this.anchor(this.getPrivacyPolicyUrl(), __('general.privacy_policy')),
            })
        }
    },
}
</script>
